import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/shared/contexts/AuthContext';
import LoadingSpinner from './LoadingSpinner';
import { isEmailVerified } from '@/shared/utils/emailVerified';

/**
 * Protected route wrapper that requires:
 * 1. Firebase authentication
 * 2. Email verification
 * 3. MongoDB profile exists
 * 4. Optionally, specific role(s)
 */
function ProtectedRoutes({ children, requiredRole }) {
    const { currentUser, userProfile, loading, hasProfile, profileLoading } =
        useAuth();
    const location = useLocation();

    // Wait for auth to resolve
    if (
        loading ||
        profileLoading ||
        (isEmailVerified(currentUser) && hasProfile === null)
    ) {
        return <LoadingSpinner />;
    }

    // Not logged in — redirect to login
    if (!currentUser) {
        const redirectTo = location.pathname + location.search;
        return (
            <Navigate
                to={`/login?redirect=${encodeURIComponent(redirectTo)}`}
                replace
            />
        );
    }

    // Email not verified — redirect to login (which shows verification message)
    if (!isEmailVerified(currentUser)) {
        return <Navigate to="/login" replace />;
    }

    // No MongoDB profile — redirect to create-profile
    if (hasProfile === false && location.pathname !== '/create-profile') {
        return <Navigate to="/create-profile" replace />;
    }

    // Has profile but on create-profile page, so redirect to the listings page
    if (hasProfile === true && location.pathname === '/create-profile') {
        return <Navigate to="/listings" replace />;
    }

    // Check role if required — deny when profile unavailable (no roles to check)
    if (requiredRole) {
        const roles = Array.isArray(requiredRole)
            ? requiredRole
            : [requiredRole];
        const userRoles = userProfile?.roles || [];
        const hasRequiredRole = roles.some((role) => userRoles.includes(role));

        // Admin bypasses role checks
        if (!hasRequiredRole && !userRoles.includes('admin')) {
            return <Navigate to="/listings" replace />;
        }
    }

    return children;
}

export default ProtectedRoutes;
